// Pure helpers for the Receiving & Racks flow (docs/cp-features/
// darkstore-cmo-receiving-db-picking.md §5, darkstore-api-contract.md §2).
// Kept side-effect-free and screen-agnostic so they're covered by
// __tests__/receivingHelpers.test.js without mocking navigation/API.

import { withGroupHeaders } from "@components/ds/groupRows";

export const TP_STATUS = {
  TO_RACK: "TO_RACK",
  IN_PROGRESS: "IN_PROGRESS",
  RACKED: "RACKED",
};

export const SHIPMENT_STATE = {
  RACKED: "RACKED",
  PENDING: "PENDING",
};

export const TABS_ORDER = [
  { key: "TO_RACK", tabsField: "to_rack", apiStatus: "TO_RACK" },
  { key: "RACKED", tabsField: "racked", apiStatus: "RACKED" },
  { key: "ALL", tabsField: "all", apiStatus: "ALL" },
];

/** `Receiving/tpList/tabs/<key>` — the i18n leaf per tab. */
export const TAB_LABEL_KEY = {
  TO_RACK: "toRack",
  RACKED: "racked",
  ALL: "all",
};

/**
 * Builds the three-tab list (with counts) the tab bar renders, in the
 * fixed "To rack / Racked / All" order regardless of key order on the
 * response.
 * @param {{to_rack?: number, racked?: number, all?: number}} tabs
 */
export function getTabsList(tabs = {}) {
  return TABS_ORDER.map(({ key, tabsField, apiStatus }) => ({
    key,
    apiStatus,
    count: Number(tabs?.[tabsField] || 0),
  }));
}

/**
 * The status chip a TP list row shows. Per the design mock (spec p2 frame
 * 2), these three are short English badge words rendered uppercase
 * regardless of app language — the same convention as the home widget's
 * "NEW" tag — so `label` here is the literal display text, not an i18n
 * key. `LABELS_MISSING` is the one exception: it's operational copy the
 * executive needs to actually read, so it stays localized (`label: null`
 * tells the caller to look up `Receiving/tpList/chip/labelsMissing`).
 * @param {{status?: string, racks?: Array<{rack_no:string}>, labels_missing?: boolean}} tp
 */
export function getTpChip(tp = {}) {
  const racks = Array.isArray(tp.racks) ? tp.racks : [];

  if (tp.labels_missing) {
    return { key: "LABELS_MISSING", tone: "disabled", label: null };
  }

  if (tp.status === TP_STATUS.RACKED) {
    if (racks.length === 1) {
      return {
        key: "RACK_SINGLE",
        tone: "racked",
        label: `RACK ${racks[0].rack_no}`,
      };
    }
    if (racks.length > 1) {
      return {
        key: "RACK_COUNT",
        tone: "racked",
        label: `${racks.length} RACKS`,
      };
    }
    return { key: "RACKED", tone: "racked", label: "RACKED" };
  }

  if (tp.status === TP_STATUS.IN_PROGRESS) {
    // A TP that has already spilled onto a second (or third…) rack: all
    // racks but the last one are necessarily full (the executive only
    // reaches for a new rack once the current one is full or chosen to be
    // marked so), so "racks.length - 1 of racks.length" reproduces the
    // mock's "1 OF 2 RACKS" for the two-rack case without needing a
    // server-side "racks needed" figure the list endpoint doesn't return.
    if (racks.length > 1) {
      return {
        key: "RACK_PROGRESS",
        tone: "inProgress",
        label: `${racks.length - 1} OF ${racks.length} RACKS`,
      };
    }
    if (racks.length === 1) {
      // Still in progress, but already sitting on a specific rack — the
      // mock's "keep the rack-number chip showing the actual rack" note
      // applies the same green treatment here as to a fully racked TP;
      // the CTA (जारी रखें vs the done tick) is what tells them apart.
      return {
        key: "RACK_SINGLE",
        tone: "racked",
        label: `RACK ${racks[0].rack_no}`,
      };
    }
    return { key: "TO_RACK", tone: "toRack", label: "TO BE RACKED" };
  }

  return { key: "TO_RACK", tone: "toRack", label: "TO BE RACKED" };
}

/**
 * The row's CTA — what pressing it does.
 * @param {{status?: string, labels_missing?: boolean}} tp
 * @returns {{key: string, action: "RACK"|"CONTINUE"|"DONE"|"DISABLED"}}
 */
export function getTpCta(tp = {}) {
  if (tp.labels_missing) return { key: "LABELS_MISSING", action: "DISABLED" };
  if (tp.status === TP_STATUS.RACKED) return { key: "DONE", action: "DONE" };
  if (tp.status === TP_STATUS.IN_PROGRESS)
    return { key: "CONTINUE", action: "CONTINUE" };
  return { key: "RACK_IT", action: "RACK" };
}

/**
 * The rack new scans land on: the last rack in `racks[]` that isn't full.
 * `racks[]` order is chronological (first rack used first), so this is the
 * TP's current/most-recent rack.
 * @param {Array<{rack_id:number, is_full?: boolean}>} racks
 */
export function getActiveRack(racks = []) {
  if (!Array.isArray(racks) || racks.length === 0) return null;
  for (let i = racks.length - 1; i >= 0; i -= 1) {
    if (!racks[i].is_full) return racks[i];
  }
  return null;
}

/**
 * Overall scan-screen state.
 * - COMPLETE: every DELIVERED box is racked — ready for "रैकिंग पूरी करें".
 * - NEEDS_NEW_RACK: more boxes to scan, but no rack has room (all full, or
 *   none chosen yet) — scan input must stay hidden until one is added.
 * - SCANNING: normal state, scans go onto the active rack.
 * @param {{scanned?: number, total?: number, racks?: Array<{is_full?: boolean}>}} roster
 */
export function getScanState({ scanned = 0, total = 0, racks = [] } = {}) {
  if (total > 0 && scanned >= total) return "COMPLETE";
  if (!getActiveRack(racks)) return "NEEDS_NEW_RACK";
  return "SCANNING";
}

/**
 * The next shipment that should be scanned — the first PENDING row in
 * server order — so exactly one row can show "SCAN NOW" and the rest show
 * plain "PENDING".
 * @param {Array<{shipment_id: string, state: string}>} shipments
 */
export function getNextPendingId(shipments = []) {
  const next = (shipments || []).find(
    (s) => s.state === SHIPMENT_STATE.PENDING,
  );
  return next ? next.shipment_id : null;
}

/**
 * Per-row status for the shipment roster.
 * @param {{shipment_id: string, state: string}} shipment
 * @param {string|null} nextPendingId
 * @returns {"RACKED"|"SCAN_NOW"|"PENDING"}
 */
export function getShipmentRowStatus(shipment, nextPendingId) {
  if (shipment?.state === SHIPMENT_STATE.RACKED) return "RACKED";
  if (shipment?.shipment_id === nextPendingId) return "SCAN_NOW";
  return "PENDING";
}

/**
 * Groups the roster by rack for the multi-rack view ("Rack B-06 — भरा हुआ ·
 * 3 shipments" / "Rack B-07 — चालू"). Returns `null` when there's at most
 * one rack in play — callers should render a flat list in that case.
 *
 * PENDING shipments (not yet racked) are attached to the active rack's
 * group, since that's where the next scan will land them.
 * @param {Array<{shipment_id:string, state:string, rack: {rack_id:number}|null}>} shipments
 * @param {Array<{rack_id:number, rack_no:string, rack_name:string, is_full?: boolean}>} racks
 */
export function groupShipmentsByRack(shipments = [], racks = []) {
  if (!Array.isArray(racks) || racks.length <= 1) return null;

  const activeRack = getActiveRack(racks);
  const groups = racks.map((rack) => ({
    rack,
    isActive: !!activeRack && rack.rack_id === activeRack.rack_id,
    shipments: (shipments || []).filter(
      (s) =>
        s.state === SHIPMENT_STATE.RACKED &&
        s.rack &&
        s.rack.rack_id === rack.rack_id,
    ),
  }));

  if (activeRack) {
    const pending = (shipments || []).filter(
      (s) => s.state === SHIPMENT_STATE.PENDING,
    );
    const activeGroup = groups.find(
      (g) => g.rack.rack_id === activeRack.rack_id,
    );
    if (activeGroup && pending.length > 0) {
      activeGroup.shipments = activeGroup.shipments.concat(pending);
    }
  }

  return groups;
}

/**
 * Trims scanner/manual input. Deliberately doesn't force-uppercase: box
 * labels (`DS0000003-01`) are already upper, and the resolve endpoint
 * upper-cases `rack_no` server-side (contract §1 "trimmed and upper-cased").
 * @param {string} value
 */
export function normalizeScanValue(value) {
  return String(value ?? "").trim();
}

export const BOX_LABEL_PATTERN = /^DS\d{7}-\d{2,}$/;
export const RACK_QR_PATTERN = /^RK\d{7}$/;

/**
 * `cx_name` is nullable (contract §2). When present it's the row/header
 * title and the `TP - <cx_id>` form moves to the meta line so the
 * executive can still match a printed/spoken TP id; when absent, `TP -
 * <cx_id>` is the title and there's no separate id line.
 * @param {{cx_name?: string|null}} tp
 */
export function getTpTitleParts(tp = {}) {
  const name = typeof tp.cx_name === "string" ? tp.cx_name.trim() : "";
  return { hasName: name.length > 0, name };
}

// Date cards (contract §2): a card is one customer's orders with one
// order_edd, so one customer can have a card per delivery date.

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const IST_OFFSET_MS = 330 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const istDay = (ms) => new Date(ms + IST_OFFSET_MS).toISOString().slice(0, 10);

/** A card's list key — `cx_id` alone is no longer unique. */
export function getTpCardKey(tp = {}) {
  return tp.date ? `${tp.cx_id}:${tp.date}` : String(tp.cx_id);
}

const RELATIVE_DAY_KEY = {
  [-1]: "dateYesterday",
  0: "dateToday",
  1: "dateTomorrow",
};

/**
 * How a card's `date` (its order_edd, YYYY-MM-DD) reads: "Delivery: Today" /
 * "Yesterday" / "Tomorrow" through the injected `translate`, else
 * "Delivery: 17 Sep". Null when the card has no date.
 * @param {string|null|undefined} date
 * @param {(key: string, params?: object) => string} translate the `t` helper
 * @param {number} now epoch ms (tests pin it)
 */
export function getCardDateText(date, translate, now = Date.now()) {
  if (!date) return null;
  const offset = [-1, 0, 1].find(
    (days) => date === istDay(now + days * DAY_MS),
  );
  const [, month, day] = date.split("-");
  const text =
    offset !== undefined
      ? translate(`Receiving/tpList/${RELATIVE_DAY_KEY[offset]}`)
      : `${Number(day)} ${MONTHS[Number(month) - 1] || month}`;
  return translate("Receiving/tpList/dateHeader", { date: text });
}

/**
 * Inserts a `DATE_HEADER` row before each new `date` in the server's
 * newest-first card list. Cards without a date (older backend) get none.
 * @param {Array<{date?: string|null}>} tps
 */
export function withDateHeaders(tps = []) {
  return withGroupHeaders(tps, {
    keyOf: (tp) => tp.date,
    type: "DATE_HEADER",
    field: "date",
    prefix: "DATE",
  });
}

// ---------- racking hub (multi-EDD, mockup §9) ----------

export const BUCKET = {
  TODAY: "TODAY",
  ADVANCE: "ADVANCE",
  PENDING: "PENDING",
};

/**
 * Per-bucket presentation: the i18n leaf under `Receiving/hub`, the English
 * tag pill (pills stay English, as the mockup shows and as Hindi pills clip)
 * and its Pill tone.
 */
export const BUCKET_META = {
  TODAY: { key: "today", tag: "LIVE FOR DB PICKING", tone: "racked" },
  ADVANCE: { key: "advance", tag: "HIDDEN FROM DB", tone: "inProgress" },
  PENDING: { key: "pending", tag: "OVERDUE", tone: "toRack" },
};

/** True when a YYYY-MM-DD date is after today (IST): an advance-racked card. */
export function isAfterToday(date, now = Date.now()) {
  return Boolean(date) && String(date) > istDay(now);
}

/** "30 Sep" for a YYYY-MM-DD date, or "" when missing. */
export function shortDate(date) {
  if (!date) return "";
  const [, month, day] = String(date).split("-");
  return `${Number(day)} ${MONTHS[Number(month) - 1] || month}`;
}

/**
 * Advance list rows: a "30 SEP · 5 TPs" header before each EDD, counts from
 * the server's `dates` (pre-search), falling back to the visible rows.
 * @param tps   cards sorted by date
 * @param dates [{ date, tps }]
 */
export function withAdvanceDateHeaders(tps = [], dates = []) {
  const countByDate = Object.fromEntries(
    (dates || []).map((row) => [row.date, row.tps]),
  );
  return withGroupHeaders(tps, {
    keyOf: (tp) => tp.date,
    type: "ADVANCE_DATE_HEADER",
    field: "date",
    prefix: "ADV",
  }).map((row) =>
    row._type === "ADVANCE_DATE_HEADER"
      ? {
          ...row,
          count:
            countByDate[row.date] ??
            tps.filter((tp) => tp.date === row.date).length,
        }
      : row,
  );
}

/**
 * The occupied-rack refusal (mockup §9 frame 05) from a resolve / scan 400:
 * RACK_OTHER_CX (another customer) or RACK_OTHER_EDD (same customer, other
 * date). Null for any other error.
 */
export function getRackRefusal(error) {
  const data = error?.response?.data || {};
  const code = data.error || data.code;
  if (code !== "RACK_OTHER_CX" && code !== "RACK_OTHER_EDD") return null;
  return {
    code,
    rackNo: data.rack_no || "",
    heldBy: data.held_by || null,
    suggestedRacks: Array.isArray(data.suggested_racks)
      ? data.suggested_racks
      : [],
  };
}
